import { useCallback, useEffect, useState } from 'react';
import { Button, Checkbox, InputNumber, Modal, Radio, Space, Spin, Switch, Tooltip, Typography, message } from 'antd';
import { CodeOutlined, CopyOutlined, ExportOutlined } from '@ant-design/icons';
import { useApi } from '../../hooks/use_api';
import { useNodeInfo } from '../../hooks/use_node_info';
import { EmbedBlock } from './EmbedBlock';
import './EmbedBlock.css';

const { Text } = Typography;

const DEFAULT_OPTIONS = { show_picture: true, show_description: true, show_join: false };

// The iframe other sites paste. Height is the embedder's to tune: a card
// fits in a few hundred pixels, an app's view wants more.
export function embedSnippet({ origin, entityId, theme, height, title }) {
    const src = `${origin}/embed/entity/${entityId}${theme === 'auto' ? '' : `?theme=${theme}`}`;
    const safeTitle = String(title || 'Vellby').replace(/"/g, '&quot;');
    return `<iframe src="${src}" width="100%" height="${height}" style="border:0;border-radius:12px" loading="lazy" title="${safeTitle}"></iframe>`;
}

/**
 * The entity page's embed controls (backend services/embed_blocks.py): the
 * switch that publishes the entity's one embed block, and, once it is on,
 * "Get code": what the block shows, a live preview of exactly what the
 * public sees (it re-reads the public route after every change), the
 * iframe snippet to paste into any site, and the block's own address.
 *
 * `entity` is the EntityResponse; `onSaved` re-reads it after a change.
 * Shown only to those who may edit the entity: the backend refuses the rest.
 */
export function EmbedControls({ entity, onSaved }) {
    const api = useApi();
    const { nodeName } = useNodeInfo();
    const [saving, setSaving] = useState(false);
    const [open, setOpen] = useState(false);
    const [theme, setTheme] = useState('auto');
    const [height, setHeight] = useState(entity.entity_type === 'app' ? 560 : 320);
    const [block, setBlock] = useState(null);
    const [loadingBlock, setLoadingBlock] = useState(false);

    const options = { ...DEFAULT_OPTIONS, ...(entity.embed_options || {}) };
    const isGroup = ['context', 'relationship'].includes(entity.entity_type);
    const origin = window.location.origin;

    const save = async (payload, label) => {
        setSaving(true);
        try {
            await api.put(`/api/entities/${entity.id}`, payload, { invalidate: `/api/entities/${entity.id}` });
            message.success(label);
            await onSaved?.();
        } catch (err) {
            message.error(err?.message || 'Could not save the embed settings');
        } finally {
            setSaving(false);
        }
    };

    // What the public sees, from the public route itself.
    const loadBlock = useCallback(() => {
        setLoadingBlock(true);
        return api.get(`/api/entities/${entity.id}/embed`)
            .then(setBlock)
            .catch(() => setBlock(null))
            .finally(() => setLoadingBlock(false));
    }, [api, entity.id]);

    useEffect(() => {
        if (open && entity.embed_enabled) loadBlock();
    }, [open, entity.embed_enabled, entity.embed_options, loadBlock]);

    const setOption = (key, value) => save({ embed_options: { ...options, [key]: value } }, 'Embed updated');

    const snippet = embedSnippet({ origin, entityId: entity.id, theme, height, title: `${entity.name} on ${nodeName || 'Vellby'}` });
    const pageUrl = `${origin}/embed/entity/${entity.id}${theme === 'auto' ? '' : `?theme=${theme}`}`;

    const copy = text => navigator.clipboard.writeText(text)
        .then(() => message.success('Copied'))
        .catch(() => message.error('Could not copy — select the text and copy it yourself'));

    return (
        <div className="embed-controls">
            <Tooltip title="Embeddable: anyone can show this entity's embed block on their own website, with the picture, description and link you allow below. Off: the block is not served to anyone.">
                <Switch
                    checked={!!entity.embed_enabled}
                    loading={saving}
                    checkedChildren="Embeddable"
                    unCheckedChildren="Not embeddable"
                    onChange={checked => save({ embed_enabled: checked }, checked ? 'Embed turned on' : 'Embed turned off')}
                />
            </Tooltip>
            {entity.embed_enabled && (
                <Button size="small" icon={<CodeOutlined />} onClick={() => setOpen(true)}>Get code</Button>
            )}
            <Modal
                className="embed-code-modal"
                open={open}
                onCancel={() => setOpen(false)}
                footer={null}
                width={620}
                title={`Embed “${entity.name}”`}
            >
                <Space direction="vertical" size={16} style={{ width: '100%' }}>
                    <div>
                        <Text strong style={{ display: 'block', marginBottom: 6 }}>Show</Text>
                        <Space wrap>
                            <Checkbox checked={options.show_picture} disabled={saving} onChange={e => setOption('show_picture', e.target.checked)}>Picture</Checkbox>
                            <Checkbox checked={options.show_description} disabled={saving} onChange={e => setOption('show_description', e.target.checked)}>Description</Checkbox>
                            {isGroup && (
                                <Tooltip title={entity.invite_token ? 'A Join button that opens this group’s invite page' : 'Create an invite link first (Connections → Invite) to offer a Join button'}>
                                    <Checkbox
                                        checked={options.show_join}
                                        disabled={saving || !entity.invite_token}
                                        onChange={e => setOption('show_join', e.target.checked)}
                                    >
                                        Join button
                                    </Checkbox>
                                </Tooltip>
                            )}
                        </Space>
                    </div>

                    <div>
                        <Text strong style={{ display: 'block', marginBottom: 6 }}>Preview</Text>
                        <div className="embed-code-modal__preview">
                            {loadingBlock && !block ? <Spin /> : block
                                ? <EmbedBlock block={block} origin={origin} nodeName={nodeName} />
                                : <Text type="secondary">The block is not being served right now.</Text>}
                        </div>
                    </div>

                    <Space wrap size={16}>
                        <span>
                            <Text strong style={{ marginRight: 8 }}>Theme</Text>
                            <Radio.Group size="small" value={theme} onChange={e => setTheme(e.target.value)}
                                options={[{ label: 'Viewer’s', value: 'auto' }, { label: 'Light', value: 'light' }, { label: 'Dark', value: 'dark' }]}
                                optionType="button" />
                        </span>
                        <span>
                            <Text strong style={{ marginRight: 8 }}>Height</Text>
                            <InputNumber size="small" min={120} max={2000} step={20} value={height} onChange={v => setHeight(v || 320)} addonAfter="px" style={{ width: 130 }} />
                        </span>
                    </Space>

                    <div>
                        <Text strong style={{ display: 'block', marginBottom: 6 }}>Paste into your site</Text>
                        <textarea readOnly value={snippet} rows={4} style={{ width: '100%', boxSizing: 'border-box', resize: 'vertical' }} onFocus={e => e.target.select()} />
                        <Space style={{ marginTop: 8 }} wrap>
                            <Button type="primary" icon={<CopyOutlined />} onClick={() => copy(snippet)}>Copy code</Button>
                            <Button icon={<CopyOutlined />} onClick={() => copy(pageUrl)}>Copy link</Button>
                            <Button icon={<ExportOutlined />} href={pageUrl} target="_blank" rel="noopener noreferrer">Open</Button>
                        </Space>
                    </div>
                </Space>
            </Modal>
        </div>
    );
}

export default EmbedControls;
